import { useRef } from "react";
import { Link } from "react-router-dom";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowRight, Sparkles } from "lucide-react";
import { PhoneMockup } from "./PhoneMockup";
import { siteConfig } from "@/config/siteConfig";

const heroChips = [
  { label: "STUDY", cls: "text-oriva-cyan", pos: "-left-8 top-14", dur: 4.5 },
  { label: "CREATE", cls: "text-oriva-gold", pos: "-right-6 top-44", dur: 5.2 },
  { label: "WORK", cls: "text-oriva-coral", pos: "-left-4 bottom-28", dur: 4.8 },
];

const lines = [
  { plain: "Study ", accent: "smarter.", underline: "decoration-oriva-cyan" },
  { plain: "Create ", accent: "better.", underline: "decoration-oriva-gold" },
  { plain: "Work ", accent: "faster.", underline: "decoration-oriva-coral" },
];

const lineVariants = {
  hidden: { y: "110%" },
  visible: (i) => ({
    y: 0,
    transition: { delay: 0.25 + i * 0.14, duration: 0.9, ease: [0.22, 1, 0.36, 1] },
  }),
};

export const Hero = () => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const ringY = useTransform(scrollYProgress, [0, 1], [0, -90]);
  const dotY = useTransform(scrollYProgress, [0, 1], [0, 70]);
  const phoneY = useTransform(scrollYProgress, [0, 1], [0, 60]);

  return (
    <section ref={ref} className="relative overflow-hidden bg-white pt-32 pb-24 sm:pt-40 sm:pb-32" data-testid="hero-section">
      <div
        className="pointer-events-none absolute inset-0"
        style={{
          backgroundImage:
            "linear-gradient(to right, rgba(15,23,42,0.04) 1px, transparent 1px), linear-gradient(to bottom, rgba(15,23,42,0.04) 1px, transparent 1px)",
          backgroundSize: "72px 72px",
        }}
      />
      <motion.div
        style={{ y: ringY }}
        className="pointer-events-none absolute -right-24 top-24 h-72 w-72 rounded-full border-[1.5px] border-oriva-blue/15"
      />
      <motion.div
        style={{ y: dotY }}
        className="pointer-events-none absolute -left-10 bottom-10 h-40 w-40 rounded-full border-[1.5px] border-oriva-cyan/20"
      />

      <div className="relative mx-auto grid max-w-6xl items-center gap-14 px-5 sm:px-8 lg:grid-cols-12">
        <div className="lg:col-span-7">
          <motion.div
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, ease: "easeOut" }}
            className="mb-8 flex flex-wrap items-center gap-3"
            data-testid="hero-eyebrow"
          >
            <span className="flex h-1.5 gap-1">
              <span className="w-6 rounded-full bg-oriva-cyan" />
              <span className="w-6 rounded-full bg-oriva-gold" />
              <span className="w-6 rounded-full bg-oriva-coral" />
            </span>
            <span className="font-mono text-xs uppercase tracking-[0.25em] text-slate-500">
              One AI. Three powerful worlds.
            </span>
            <span
              className="rounded-full border border-oriva-gold/40 bg-oriva-gold/10 px-3.5 py-1 text-xs font-semibold text-amber-700"
              data-testid="hero-hindi-tagline"
            >
              {siteConfig.brand.hindiTagline}
            </span>
          </motion.div>

          <h1 className="font-display text-4xl font-semibold tracking-tight text-slate-900 sm:text-5xl lg:text-6xl" data-testid="hero-headline">
            {lines.map((line, i) => (
              <span key={i} className="block overflow-hidden pb-1">
                <motion.span
                  className="block"
                  custom={i}
                  variants={lineVariants}
                  initial="hidden"
                  animate="visible"
                >
                  {line.plain}
                  <span className={`underline decoration-[5px] underline-offset-8 ${line.underline}`}>
                    {line.accent}
                  </span>
                </motion.span>
              </span>
            ))}
          </h1>

          <motion.p
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.85, duration: 0.7, ease: "easeOut" }}
            className="mt-8 max-w-xl text-base leading-relaxed text-slate-600 sm:text-lg"
            data-testid="hero-subcopy"
          >
            ORIVA combines a universal AI assistant with specialised toolkits for students,
            creators and professionals — one app that studies with you, creates with you
            and works with you.
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 1, duration: 0.7, ease: "easeOut" }}
            className="mt-10 flex flex-wrap items-center gap-4"
          >
            <Link
              to="/#app"
              data-testid="hero-cta-get-oriva"
              className="inline-flex items-center gap-2 rounded-full bg-oriva-blue px-7 py-3.5 text-sm font-medium text-white transition-colors duration-200 hover:bg-blue-700"
            >
              Get ORIVA
              <ArrowRight className="h-4 w-4" />
            </Link>
            <Link
              to="/#worlds"
              data-testid="hero-cta-explore"
              className="inline-flex items-center gap-2 rounded-full border border-slate-300 px-7 py-3.5 text-sm font-medium text-slate-800 transition-colors duration-200 hover:border-slate-400 hover:bg-slate-50"
            >
              Explore Features
            </Link>
            <span className="flex items-center gap-2 font-mono text-xs uppercase tracking-[0.2em] text-slate-400">
              <Sparkles className="h-3.5 w-3.5 text-oriva-blue" />
              Android · Google Play
            </span>
          </motion.div>
        </div>

        <div className="hidden lg:col-span-5 lg:block" style={{ perspective: "1200px" }}>
          <motion.div style={{ y: phoneY }} className="relative mx-auto max-w-[300px]">
            <PhoneMockup
              src="/assets/app-chat.webp"
              alt="ORIVA app — Universal AI Chat"
              tilt={6}
              priority
            />
            {heroChips.map((chip) => (
              <motion.span
                key={chip.label}
                animate={{ y: [0, -8, 0] }}
                transition={{ duration: chip.dur, repeat: Infinity, ease: "easeInOut" }}
                className={`absolute ${chip.pos} rounded-full border border-slate-200 bg-white/80 px-4 py-1.5 font-mono text-[11px] uppercase tracking-[0.2em] backdrop-blur-md ${chip.cls}`}
              >
                {chip.label}
              </motion.span>
            ))}
          </motion.div>
        </div>
      </div>
    </section>
  );
};
